import { DateTime } from "./imports";
import {
  currentState,
  minimumRefreshTime,
  observatoryMetadata,
} from "./config";
import { switchObservatory } from "./initialize";
import { maxDataPoints } from "./graphs/base_graphs";

/**
 * Get the two-letter code of the current observatory
 * @returns {string}
 */
export function getCurrentObservatoryName() {
  return currentState.currentObservatory;
}

/**
 * Update the current observatory from a click event
 * @param {click} event
 */
export function setCurrentObservatory(event) {
  currentState.currentObservatory = event.currentTarget.value;
  switchObservatory();
}

/**
 * Check if the refresh timeout has passed
 * @returns {boolean} - whether to allow refresh
 */
export function allowRefresh() {
  const now = DateTime.now();

  // If there exists a last refresh time
  if (currentState.lastRefresh != null) {
    // diff against current
    const diff = now.diff(currentState.lastRefresh).as("seconds");
    if (diff < minimumRefreshTime) {
      return false;
    }
  }

  return true;
}

/**
 * Convert the zone-less ISO strings Ramotswe returns into proper ISO strings
 * @param {string} datetime_utc
 * @returns {string}
 */
export function parseRamotsweDatetime(datetime_utc) {
  if (datetime_utc === undefined) {
    return null;
  }
  return DateTime.fromISO(datetime_utc, { zone: "utc" }).toISO();
}

/**
 * Get the earliest Luxon DateTime from an array
 * @param {DateTime[]} datetime_array
 * @returns {DateTime}
 */
export function getEarliestDate(datetime_array) {
  return datetime_array.sort()[0];
}

/**
 * Return a ramotswe data array with times converted to local
 *
 * @param {Array} dataArray
 * @returns {Array} data array with changed time values
 */
export function changeBinnedTimeSeriesUTCTimeToLocalTime(dataArray) {
  if (dataArray === null) {
    return null;
  }

  const timeZone = observatoryMetadata[getCurrentObservatoryName()].TZInfo;

  dataArray.forEach((element) => {
    element["time_bin_center"] = changeUTCTimeToLocalTime(
      element["time_bin_center"],
      timeZone
    );
    element["time_bin_edges"][0] = changeUTCTimeToLocalTime(
      element["time_bin_edges"][0],
      timeZone
    );
    element["time_bin_edges"][1] = changeUTCTimeToLocalTime(
      element["time_bin_edges"][1],
      timeZone
    );
  });

  return dataArray;
}

/**
 * Return a ramotswe data array with times converted to local
 *
 * @param {Array} dataArray
 * @returns {Array} data array with changed time values
 */
export function changeTimeSeriesUTCTimeToLocalTime(dataArray) {
  if (dataArray === null) {
    return null;
  }
  const timeZone = observatoryMetadata[getCurrentObservatoryName()].TZInfo;

  dataArray.forEach((element) => {
    element["datetime_utc"] = changeUTCTimeToLocalTime(
      element["datetime_utc"],
      timeZone
    );
  });

  return dataArray;
}

/**
 * Takes a string in ISO format (assumed to be UTC) and turns it into an ISO string
 * with the specified timezone.
 *
 * @param {string} dateString
 * @param {string} timeZone
 * @returns {string} new ISO format date
 */
export function changeUTCTimeToLocalTime(dateString, timeZone) {
  const UTCDate = DateTime.fromISO(dateString, { zone: "utc" }).setZone(
    timeZone
  );

  const UTCString = UTCDate.toFormat("y-LL-dd") + "T" + UTCDate.toFormat("TT");
  // const UTCString = UTCDate.toISO();
  return UTCString;
}

/**
 * Create a bootstrap spinner
 * @returns {Node}
 */
export function createLoadingSpinner() {
  const loadingSpinner = document.createElement("div");
  loadingSpinner.classList.add("spinner-border");
  loadingSpinner.setAttribute("role", "status");

  // Add an accessibility label inside the spinner
  const spinnerLabel = document.createElement("span");
  spinnerLabel.classList.add("sr-only");
  spinnerLabel.innerText = "Loading";
  loadingSpinner.appendChild(spinnerLabel);

  return loadingSpinner;
}

/**
 * Create a dataset that looks like a ramotswe response with null data
 * @param {string} yKey
 * @param {DateTime} startDateTime
 * @param {DateTime} endDateTime
 * @returns {Object[]}
 */
export function createNullBinnedTimeSeriesData(
  yKey,
  startDateTime,
  endDateTime
) {
  const durationSeconds =
    endDateTime.diff(startDateTime).as("seconds") / maxDataPoints;
  const nullData = [];

  // create an entry in nullData with the correct time gaps
  for (let i = 0; i <= maxDataPoints; i += 1) {
    const time_bin_center = startDateTime.plus({
      seconds: durationSeconds * i,
    });
    const time_bin_edges = [
      time_bin_center,
      time_bin_center.plus({ seconds: durationSeconds }),
    ];
    const entry = {
      time_bin_center: time_bin_center.toISO(),
      time_bin_edges: [time_bin_edges[0].toISO(), time_bin_edges[1].toISO()],
    };
    entry[yKey] = null;
    nullData.push(entry);
  }

  return nullData;
}

/**
 * Get the bin edges from a Ramotswe binned time series data response
 * @param {Object[]} dataArray
 * @returns {string[]}
 */
export function getBinnedXValues(dataArray) {
  const xValues = [dataArray.at(0).time_bin_edges[0]];
  dataArray.forEach((d) => xValues.push(d.time_bin_edges[1]));
  return xValues;
}

/**
 * Get the desired x limits of a graph, based on the x value of the data
 * @param {string[]} xValues
 * @param {Number} [padPercent=0.75]
 * @returns {string[]}
 */
export function getXLims(xValues, padPercent = 0.75) {
  const firstEdge = DateTime.fromISO(xValues.at(0), { zone: "UTC" });
  const secondEdge = DateTime.fromISO(xValues.at(1), { zone: "UTC" });
  const lastEdge = DateTime.fromISO(xValues.at(-1), { zone: "UTC" });

  const padSeconds = secondEdge.diff(firstEdge).as("seconds") * padPercent;
  const leftEdge = firstEdge.minus({ seconds: padSeconds });
  const rightEdge = lastEdge.plus({ seconds: padSeconds });

  const toFormat = (dt) => dt.toFormat("y-LL-dd") + "T" + dt.toFormat("TT");
  return [toFormat(leftEdge), toFormat(rightEdge)];
}

/**
 * Get the widest coverage from an array of limit arrays
 * @param {Number[][]} allLimits
 * @returns {Number[]}
 */
export function getWidestXLimits(allLimits) {
  const wideLimits = [...allLimits[0]];
  allLimits.forEach((limits) => {
    if (limits[0] < wideLimits[0]) {
      wideLimits[0] = limits[0];
    }
    if (limits[1] > wideLimits[1]) {
      wideLimits[1] = limits[1];
    }
  });
  return wideLimits;
}

/**
 * For each element of dataArray, if the keys in yKeys don't exist, set them to null
 * @param {Object[]} dataArray
 * @param {string[]} yKeys
 * @returns {Object[]}
 */
export function ensureYKeyPresence(dataArray, yKeys) {
  dataArray.forEach((row) => {
    yKeys.forEach((key) => {
      if (!row.hasOwnProperty(key)) {
        row[key] = null;
      }
    });
  });
  return dataArray;
}

/**
 * Remove rows of dataArray where there is no data, only for rows at the start and end
 * @param {Object[]} dataArray
 * @param {string[]} yKeys
 * @returns {Object[]}
 */
export function trimNullEdges(dataArray, yKeys) {
  // if all rows are null for all keys, do nothing
  if (yKeys.every((key) => dataArray.every((row) => row[key] == null))) {
    return dataArray;
  }

  const startIndex = dataArray.findIndex(
    (row) => !yKeys.every((key) => row[key] === null)
  );
  const endIndex = dataArray.findLastIndex(
    (row) => !yKeys.every((key) => row[key] === null)
  );

  return dataArray.slice(startIndex, endIndex + 1);
}

/**
 * Swap `potentialData` with "N/A" if it's not display-worthy
 * @param {Any} potentialData
 * @param {string} [alternativeText="N/A"]
 * @returns {Any}
 */
export function cleanNullData(potentialData, alternativeText = "N/A") {
  return potentialData ? potentialData : alternativeText;
}

/**
 * Set the first character of a string to uppercase
 * @param {string} string
 * @returns {string}
 */
export function startUpperCase(string) {
  return string[0].toUpperCase() + string.slice(1);
}

/**
 * Add `desiredClass` to `element`, and remove all other classes in `classArray`
 * @param {Node} element
 * @param {string} desiredClass
 * @param {string[]} classArray
 */
export function setClassFromList(element, desiredClass, classArray) {
  for (let className of classArray) {
    if (className === desiredClass) {
      element.classList.add(className);
    } else {
      element.classList.remove(className);
    }
  }
}

export function removeChildrenIfPopulated(elementSelector) {
  const targetElement = document.querySelector(elementSelector);
  if (targetElement.childNodes.length > 0) {
    targetElement.childNodes.forEach((element) => {
      targetElement.removeChild(element);
    });
  }
}

/**
 * Set the class of a dome/door open/closed element based on the element text, and
 * whether the observatory has Musca
 * @param {Node} element
 * @param {boolean} dataExpected
 */
export function setOpenClosedElementClass(element, observatoryName) {
  const dataExpected = observatoryMetadata[observatoryName].endpoints.musca;
  const classes = [
    "dome-open",
    "dome-part-open",
    "dome-closed",
    "no-data-expected",
    "unexpected-missing-data",
  ];

  if (!dataExpected) {
    setClassFromList(element, "no-data-expected", classes);
    return;
  }

  if (element.textContent === "Open") {
    setClassFromList(element, "dome-open", classes);
  } else if (element.textContent === "Part open") {
    setClassFromList(element, "dome-part-open", classes);
  } else if (element.textContent === "Closed") {
    setClassFromList(element, "dome-closed", classes);
  } else {
    setClassFromList(element, "unexpected-missing-data", classes);
  }
}
